import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { timsurveyApi } from '../../services/api'
import {
  EsitoTimsurvey, ScenarioFirmaTimsurvey, SituazioneTimsurvey,
  TimsurveyPraticaDettaglio, TimsurveyVerbaleUpsertBody, UscitaVuotoMotivoTimsurvey,
} from '../../types/timsurvey'
import StepRiepilogo from './wizard/StepRiepilogo'
import StepEsito from './wizard/StepEsito'
import StepSituazione from './wizard/StepSituazione'
import StepVociListino from './wizard/StepVociListino'
import StepPlanimetriaNote from './wizard/StepPlanimetriaNote'
import StepConferma from './wizard/StepConferma'
import StepFirme from './wizard/StepFirme'
import StepFine from './wizard/StepFine'

export interface WizardFormState {
  data_sopralluogo_effettiva: string
  esito: EsitoTimsurvey | null
  uscita_vuoto_motivo: UscitaVuotoMotivoTimsurvey | null
  uscita_vuoto_altro_descrizione: string
  situazione: SituazioneTimsurvey | null
  rilievi: { casistica_code: string; quantita: number | null }[]
  fuori_standard_altro_descrizione: string
  voci_listino: { nmu_code: string; quantita: number }[]
  note_aggiuntive: string
  scenario_firma: ScenarioFirmaTimsurvey | null
}

const FORM_INIZIALE: WizardFormState = {
  data_sopralluogo_effettiva: new Date().toISOString().slice(0, 16),
  esito: null,
  uscita_vuoto_motivo: null,
  uscita_vuoto_altro_descrizione: '',
  situazione: null,
  rilievi: [],
  fuori_standard_altro_descrizione: '',
  voci_listino: [],
  note_aggiuntive: '',
  scenario_firma: null,
}

export type SalvaBozza = () => Promise<void>

export interface StepProps {
  pratica: TimsurveyPraticaDettaglio
  form: WizardFormState
  setForm: React.Dispatch<React.SetStateAction<WizardFormState>>
  onNext: () => void
  onBack: () => void
  isFirstStep: boolean
  salvaBozza: SalvaBozza
}

const STEP_LABELS = [
  'Riepilogo', 'Esito', 'Situazione', 'Voci listino', 'Planimetria/Note', 'Conferma', 'Firme', 'Fine',
]

function buildUpsertBody(form: WizardFormState): TimsurveyVerbaleUpsertBody {
  return {
    data_sopralluogo_effettiva: form.data_sopralluogo_effettiva
      ? new Date(form.data_sopralluogo_effettiva).toISOString()
      : null,
    esito: form.esito,
    uscita_vuoto_motivo: form.uscita_vuoto_motivo,
    uscita_vuoto_altro_descrizione: form.uscita_vuoto_altro_descrizione || null,
    situazione: form.situazione,
    fuori_standard_altro_descrizione: form.fuori_standard_altro_descrizione || null,
    note_aggiuntive: form.note_aggiuntive || null,
    scenario_firma: form.scenario_firma,
    rilievi: form.rilievi,
    voci_listino: form.voci_listino,
  }
}

export default function TimsurveyWizard() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState(0)
  const [form, setForm] = useState<WizardFormState>(FORM_INIZIALE)

  const { data: pratica, isLoading } = useQuery({
    queryKey: ['timsurvey-pratica', id],
    queryFn: () => timsurveyApi.pratiche.get(id!).then(r => {
      const dettaglio = r.data as TimsurveyPraticaDettaglio
      if (dettaglio.verbale) {
        setForm(f => ({
          ...f,
          esito: dettaglio.verbale!.esito,
          situazione: dettaglio.verbale!.situazione,
          scenario_firma: dettaglio.verbale!.scenario_firma,
        }))
      }
      return dettaglio
    }),
    enabled: !!id,
  })

  const salvaBozza: SalvaBozza = async () => {
    try {
      await timsurveyApi.verbale.upsert(id!, buildUpsertBody(form))
      queryClient.invalidateQueries({ queryKey: ['timsurvey-pratica', id] })
    } catch (err: any) {
      toast.error(err.response?.data?.detail ?? 'Errore salvataggio bozza')
    }
  }

  const STEP_ESITO_INDEX = 1
  const STEP_CONFERMA_INDEX = 5

  const onNext = async () => {
    await salvaBozza()
    if (currentStep === STEP_ESITO_INDEX && form.esito === 'USCITA_A_VUOTO') {
      setCurrentStep(STEP_CONFERMA_INDEX)
      return
    }
    setCurrentStep(s => Math.min(s + 1, STEP_LABELS.length - 1))
  }

  const onBack = () => setCurrentStep(s => Math.max(s - 1, 0))

  if (isLoading || !pratica) {
    return <div className="card p-8 text-center text-sm text-slate-400">Caricamento...</div>
  }

  if (pratica.stato === 'CHIUSA' || pratica.stato === 'ANNULLATA') {
    return (
      <div className="card p-8 text-center text-sm text-slate-500">
        Pratica non modificabile (stato {pratica.stato}).
      </div>
    )
  }

  const stepProps: StepProps = {
    pratica, form, setForm, onNext, onBack, isFirstStep: currentStep === 0, salvaBozza,
  }

  const STEPS = [
    StepRiepilogo, StepEsito, StepSituazione, StepVociListino,
    StepPlanimetriaNote, StepConferma, StepFirme, StepFine,
  ]
  const StepComponent = STEPS[currentStep]

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {STEP_LABELS.map((label, i) => (
          <div
            key={label}
            className="w-3 h-3 rounded-full"
            style={{ background: i === currentStep ? '#1E40AF' : i < currentStep ? '#93c5fd' : '#e5e7eb' }}
            title={label}
          />
        ))}
      </div>
      <div className="text-center text-sm text-slate-500 font-medium">{STEP_LABELS[currentStep]}</div>
      <StepComponent {...stepProps} />
    </div>
  )
}
